'use client';

import { useCallback } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { UserMenuResetOnboarding } from '@/components/user-menu-reset-onboarding';
import { UserMenuDebugPanel } from '@/components/user-menu-debug-panel';
import { UserMenuDebugIndicators } from '@/components/user-menu-debug-indicators';
import { isDevMode, shouldShowDebugTools } from '@/lib/dev-mode';
import { logger } from '@/lib/logger';
import Link from 'next/link';

interface UserMenuWrapperProps {
  user?: { name?: string; picture?: string; email?: string };
}

/**
 * Client component wrapper for user menu dropdown
 * Handles onOpenChange logging for debugging dropdown interactions
 * 
 * React 19 Best Practices:
 * - Uses useCallback for event handler to prevent unnecessary re-renders
 * - Proper dependency array includes user?.email to avoid stale closures
 * - Early return pattern for conditional rendering (handled by child components)
 */
export function UserMenuWrapper({ user }: UserMenuWrapperProps) {
  // React 19: useCallback for event handlers to prevent re-renders
  // MUST be called before any early returns (Rules of Hooks)
  const handleOpenChange = useCallback((open: boolean) => {
    // Dropdown state changed (details in app logs)
    // Only log closure for important events
    if (!open) {
      logger.info('User menu closed', { userEmail: user?.email });
    }
  }, [user?.email]);

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="overflow-hidden rounded-full"
          data-tour="user-menu"
        >
          <Avatar className="h-9 w-9">
            <AvatarImage
              src={typeof user?.picture === "string" ? user.picture : undefined}
              alt={typeof user?.name === "string" ? user.name : "Avatar"}
            />
            <AvatarFallback>
              {typeof user?.name === "string" ? user.name.charAt(0).toUpperCase() : "U"}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>My Account</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled>
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem disabled>
          Support
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <UserMenuResetOnboarding />
        <UserMenuDebugPanel />
        <UserMenuDebugIndicators />
        {(shouldShowDebugTools() || isDevMode()) && <DropdownMenuSeparator />}
        <DropdownMenuItem>
          {isDevMode() ? (
            <span className="text-muted-foreground">Dev Mode - No Auth</span>
          ) : (
            <Link href="/auth/logout" prefetch={false}>Logout</Link>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

